// components/EventsList.jsx - Mobile-optimized professional layout
import { Calendar, CheckCircle, AlertCircle, Clock } from 'lucide-react';

export function EventsList({ currentEvents, historicalEvents, requiredAmount, startingBalance }) {
  // Combine events - current first, then historical (reverse chronological)
  const events = [...(currentEvents || []), ...(historicalEvents || [])];
  
  if ((!events || events.length === 0) && !startingBalance) {
    return (
      <div className="bg-white rounded-2xl shadow-md p-4 sm:p-6">
        <div className="flex items-center gap-2 sm:gap-3 mb-4">
          <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400" />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800">Events</h2>
        </div>
        <p className="text-center py-8 text-gray-500">No events to display</p>
      </div>
    );
  }

  const getStatusIcon = (event) => {
    if (event.isPaid) {
      return <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 flex-shrink-0" />;
    }
    if (event.category === 'current') {
      return <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 flex-shrink-0" />;
    }
    if (event.isPartial) {
      return <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500 flex-shrink-0" />;
    }
    return <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0" />;
  };

  const getStatusText = (event) => {
    // For paid historical events
    if (event.isPaid) {
      return `Paid: $${requiredAmount}`;
    }
    
    // For current events - show only due date, no amount
    if (event.category === 'current') {
      const endDate = new Date(event.endDate);
      const dueDate = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      if (event.coveredByCredit) {
        return `Due ${dueDate}`;
      }
      return `Due ${dueDate}`;
    }
    
    // Historical unpaid - show what is still owed.
    // A partly paid event carries a smaller amount than the full cost.
    const due = event.amountDue != null ? event.amountDue : requiredAmount;
    return `Amt Due: $${due}`;
  };

  const getStatusColor = (event) => {
    if (event.isPaid) return 'text-green-600';
    if (event.category === 'current' && event.coveredByCredit) return 'text-blue-600';
    if (event.isPartial) return 'text-orange-600';
    return 'text-red-600';
  };

  const formatEventName = (event) => {
    // For current events: just name (no date)
    if (event.category === 'current') {
      return event.name;
    }
    
    // For historical events: name + date
    const eventDate = new Date(event.endDate);
    const monthDay = eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${event.name} - ${monthDay}`;
  };

  // Group under year headings only when the list spans more than one year
  const eventYear = (event) => new Date(event.endDate).getFullYear();
  const distinctYears = [...new Set(events.map(eventYear))];
  const showYearHeadings = distinctYears.length > 1;

  // Build display rows: an optional year heading followed by that year's events
  const rows = [];
  let lastYear = null;
  events.forEach(event => {
    const year = eventYear(event);
    if (showYearHeadings && year !== lastYear) {
      rows.push({ type: 'year', year });
      lastYear = year;
    }
    rows.push({ type: 'event', event });
  });

  return (
    <div className="bg-white rounded-2xl shadow-md overflow-hidden">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b">
        <div className="flex items-center gap-2 sm:gap-3">
          <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 flex-shrink-0" />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800">Events</h2>
          <span className="ml-auto bg-blue-100 text-blue-700 text-xs sm:text-sm font-semibold px-2.5 sm:px-3 py-1 rounded-full">
            {events.length + (startingBalance ? 1 : 0)}
          </span>
        </div>
      </div>

      {/* Events List */}
      <div className="divide-y divide-gray-200">
        {rows.map(row => {
          if (row.type === 'year') {
            return (
              <div
                key={`year-${row.year}`}
                className="px-4 sm:px-6 py-2 bg-gray-50"
              >
                <span className="text-xs sm:text-sm font-bold text-gray-500 tracking-wide">
                  {row.year}
                </span>
              </div>
            );
          }

          const event = row.event;
          return (
            <div 
              key={event.id} 
              className="px-4 sm:px-6 py-4 sm:py-5 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3 sm:gap-4">
                {/* Status Icon */}
                {getStatusIcon(event)}
                
                {/* Event Name - takes available space */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm sm:text-base font-semibold text-gray-800 truncate">
                    {formatEventName(event)}
                  </div>
                </div>
                
                {/* Status Text - fixed width on right */}
                <div className={`text-sm sm:text-base font-semibold whitespace-nowrap ${getStatusColor(event)}`}>
                  {getStatusText(event)}
                </div>
              </div>
            </div>
          );
        })}

        {/* Prior year balance - always last */}
        {startingBalance && (
          <div className="px-4 sm:px-6 py-4 sm:py-5 bg-gray-50">
            <div className="flex items-center gap-3 sm:gap-4">
              {startingBalance.isPaid ? (
                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 flex-shrink-0" />
              ) : (
                <AlertCircle className={`w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 ${startingBalance.isPartial ? 'text-orange-500' : 'text-red-600'}`} />
              )}

              <div className="flex-1 min-w-0">
                <div className="text-sm sm:text-base font-semibold text-gray-800 truncate">
                  {startingBalance.label}
                </div>
              </div>

              <div className={`text-sm sm:text-base font-semibold whitespace-nowrap ${
                startingBalance.isPaid
                  ? 'text-green-600'
                  : startingBalance.isPartial
                    ? 'text-orange-600'
                    : 'text-red-600'
              }`}>
                {startingBalance.isPaid
                  ? `Paid: $${startingBalance.amount}`
                  : `Amt Due: $${startingBalance.amountDue}`}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EventsList;